/** `useBatchAction` — wraps the generic `/batch/` dispatch in a mutation with
 *  the standard success/error UX so every grid behaves identically.
 *
 *  On a 202 (queued): a success snackbar ("<label> started — follow progress in
 *  Background Tasks", plus a capped note when the server truncated the set) with
 *  a **View task** button deep-linking the dispatched task, the grid's query
 *  invalidated, and the selection cleared. On error: an error snackbar with the
 *  API message.
 *
 *  An action that opts into `refusals` (the server may refuse it item by item)
 *  also says how many the server left out of the task, and a refusal of the whole
 *  selection becomes `refusal` — for the caller's BatchRefusalDialog — instead of
 *  the error snackbar. The selection is kept then: nothing was queued.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { runBatch, type BatchResponse, type BatchSelection } from '@/api/batch';
import { ConsoleApiError } from '@/api/client';
import { useSnackbar } from '@/components/Snackbar';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';

import type { BatchRefusal, BatchRefusals } from './batchTypes';

/** Longer than the 5s default: the snackbar carries an action, and it is only
 *  useful while it is still on screen to be clicked. Matches the other
 *  action-bearing snackbars (ProductSyncForm, watchExportTask). */
const QUEUED_AUTOHIDE_MS = 15_000;

export interface UseBatchActionOptions {
  /** URL segment of the resource (e.g. `regions`). */
  resource: string;
  /** The grid's TanStack query key root — invalidated on success. */
  queryKey: readonly unknown[];
  /** Drop the grid selection on success. */
  onCleared: () => void;
}

export interface RunBatchArgs {
  action: string;
  /** Human label for the snackbar ("Delete", "Publish", …). */
  label: string;
  selection: BatchSelection;
  params?: Record<string, unknown>;
  /** The grid's current list query string (no leading `?`). */
  filterQuery?: string;
  /** The action's opt-in refusal reporting (BatchActionDef.refusals). */
  refusals?: BatchRefusals;
}

/** Build the "queued" snackbar copy, noting the cap when the server truncated
 *  and, for a refusing action, what it left out. */
function successMessage(label: string, res: BatchResponse, refusals?: BatchRefusals): string {
  const base = `${label} started — follow progress in Background Tasks.`;
  // An action refused only as a whole has nothing to add (`leftOut` answers '').
  const join = (head: string, tail: string) => (tail ? `${head} ${tail}` : head);
  if (res.capped) {
    const capped = `${base} (Only the first ${res.processed.toLocaleString()} of ${res.matched.toLocaleString()} were queued.)`;
    // The cap and the refusals both shrink `processed`: how many were refused is unknown.
    return refusals ? join(capped, refusals.leftOut(null)) : capped;
  }
  const leftOut = res.matched - res.processed;
  return refusals && leftOut > 0 ? join(base, refusals.leftOut(leftOut)) : base;
}

export function useBatchAction({ resource, queryKey, onCleared }: UseBatchActionOptions) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [refusal, setRefusal] = useState<BatchRefusal | null>(null);

  const mutation = useMutation<BatchResponse, unknown, RunBatchArgs>({
    mutationFn: ({ action, selection, params, filterQuery }) =>
      runBatch(resource, action, selection, params, filterQuery),
    onSuccess: (res, { label, refusals }) => {
      snackbar.show(successMessage(label, res, refusals), 'success', {
        autoHideDuration: QUEUED_AUTOHIDE_MS,
        action: { label: 'View task', onClick: () => openBackgroundTask(res.task_id) },
      });
      qc.invalidateQueries({ queryKey });
      onCleared();
    },
    onError: (e, { refusals }) => {
      const refused = refusals?.refused(e) ?? null;
      if (refused) {
        setRefusal(refused);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Batch action failed.', 'error');
    },
  });

  /** Fire a batch action and resolve once the 202 has been handled (so a custom
   *  dialog can close on completion). Never rejects to the caller — errors are
   *  surfaced via the snackbar (or `refusal`). */
  const run = useCallback(
    (args: RunBatchArgs): Promise<void> => mutation.mutateAsync(args).then(() => undefined, () => undefined),
    [mutation],
  );

  const dismissRefusal = useCallback(() => setRefusal(null), []);

  return { run, isPending: mutation.isPending, refusal, dismissRefusal };
}
